<template>
  <div
    id="apartment"
    style="overflow: hidden"
    ref="apartmentContainer"
    class="apartment-component-container"
    @mousedown="captureOn"
    @mouseup="captureOff"
    @mousemove="move"
    @touchstart="touchStart"
    @touchend="touchEnd"
    @touchmove="touchMove"
    :style="`background-color: ${mainBgColor};`"
  >

    <Buttons>
      <Compass :rotate="compassRotate" />
    </Buttons>
    <Buttons v-if="apartmentFloorsQuantity > 1" align="middle-right">
      <h5>{{ $t('Floor') }}</h5>
      <ButtonGroup :reverse="true">
        <Button
          v-for="n in floorsArr"
          :disabled="currentLoadingImagesModuloN === 1 ? false : true"
          v-bind:key="n"
          :class="currentFloor === n ? 'button--floors active' : 'button--floors'"
          @click.native.prevent="currentFloor !== n && changeFloor(n)"
          >{{ n }}</Button
        >
      </ButtonGroup>
    </Buttons>

    <div class="apartment-wrapper" :style="`background-color: ${mainBgColor}; transform: translateY(${currentTranslateYFrames}%)`">
      <div class="apartment-view">
        <div class="frames">
          <div v-for="n in [...floorsArr].reverse()" v-bind:key="n" class="frame-wrapper" ref="frameWrappers" :style="`background-color: ${mainBgColor};`">
            <img
              :style="`transform: scale(${roundedZoomValue}); transform-origin: ${imageTransformOrigin};`"
              :src="frames[n][activeFrames[n] - 1].src"
              alt
              draggable="false"
            />
          </div>
        </div>
      </div>
    </div>
    <Buttons align="bottom">
      <Button @click.native.prevent="prev" class="button--rotate">
        <img src="@/assets/arrow.svg" alt="Prev rotate" />
      </Button>
      <Button @click.native.prevent="next" class="button--rotate">
        <img src="@/assets/arrow.svg" alt="Next rotate" style="transform: rotate(180deg)" />
      </Button>
      <ButtonGroup :vertical="true">
        <Button @click.native.prevent="zoomIn(desktop ? scaling.zoomGap : scaling.mobileZoomGap)" class="button--scale button--scale--plus" size="small" />
        <Button @click.native.prevent="zoomOut(desktop ? scaling.zoomGap : scaling.mobileZoomGap)" class="button--scale button--scale--minus" size="small" />
      </ButtonGroup>
    </Buttons>

    <Button @click.native.prevent="toggleFullScreenMode" class="button--full-screen">
      <img :src="isFullScreenMode ? require('@/assets/exit-full-screen.svg') : require('@/assets/open-full-screen.svg')" alt="Full screen mode" />
    </Button>

    <LoadingFrames v-if="currentLoadingImagesModuloN > 1" :count="`${loadedFramesCount}/${allFramesCount}`" />

    <YslabLogo />

    <RotateTooltip
      v-if="isTooltipRotateVisible"
      @click="closeRotateTooltip"
    />
  </div>
</template>

<script>
import Button from "@/components/Button"
import Buttons from "@/components/Buttons"
import ButtonGroup from "@/components/ButtonGroup"
import Compass from "@/components/Compass"
import LoadingFrames from "@/components/LoadingFrames"
import YslabLogo from "@/components/YslabLogo"
import RotateTooltip from './RotateTooltip.vue'
import { getAbsoluteMovementX, userIsSwipingLeft, userIsSwipingRight } from "../utils/rotation"
import { verQuery } from "../utils/assetsVersionForCache"

export default {
  name: "Apartment",
  data() {
    return {
      rotating: false,
      captureToggle: false,
      x: undefined,
      framesIntervalMs: 20,
      frames: [],
      loadedFrames: [],
      startFromFrame: 1,
      activeFrames: [],
      allFramesLoaded: false,
      loadedFramesCount: 0,
      allFramesCount: 0,
      currentFrameResolution: null,
      resizeDebounceTimeout: null,

      loadingSteps: [
        {
          allImagesLoaded: false,
          loadingFramesCount: 0,
        },
        {
          allImagesLoaded: false,
          loadingFramesCount: 0,
        },
        {
          allImagesLoaded: false,
          loadingFramesCount: 0,
        },
      ],
      currentLoadingStep: 0,
      currentLoadingImagesModuloN: 4,
      loadingProgressData: {
        loadedImagesCount: 0,
        allImagesCount: 0,
      },

      compassRotate: 0,
      quantityOfViews: this.investmentMeta.quantityOfViews,

      scaling: {
        defaultZoom: this.investmentMeta.scaling.defaultZoom,
        defaultPortraitMobileZoom: this.investmentMeta.scaling.defaultPortraitMobileZoom,
        defaultLandscapeMobileZoom: this.investmentMeta.scaling.defaultLandscapeMobileZoom,

        maxZoom: this.investmentMeta.scaling.maxZoom,
        maxPortraitMobileZoom: this.investmentMeta.scaling.maxPortraitMobileZoom,
        maxLandscapeMobileZoom: this.investmentMeta.scaling.maxLandscapeMobileZoom,

        minZoom: this.investmentMeta.scaling.minZoom,
        minPortraitMobileZoom: this.investmentMeta.scaling.minPortraitMobileZoom,
        minLandscapeMobileZoom: this.investmentMeta.scaling.minLandscapeMobileZoom,

        zoomGap: this.investmentMeta.scaling.zoomGap,
        mobileZoomGap: this.investmentMeta.scaling.mobileZoomGap,
      },
      pinchScaling: {
        isActive: false,
        startFingersDistance: 0,
        zoomGap: 0.08,
      },
      isLandscape: false,
      isTooltipRotateVisible: true,
      isFullScreen: false,
      imageTransformOrigin: "center",

      apartmentFloorsQuantity: this.apartmentMeta.quantityOfFloors,
      floorsArr: [],
      previousFloor: 0,
      currentFloor: "",
      floorsWereLoaded: [],
      currentTranslateYFrames: -(100 - 100 / this.apartmentMeta.quantityOfFloors),

      arrOfCompassValues: [],



      animation: {
        pixelsPerOneFrame: this.investmentMeta.animation.pixelsPerOneFrame || 13,
        mouse: {
          lastClientX: null
        },
        touch: {
          movementX: null,
          lastClientX: null,
          previousTouch: null
        }
      }
    }
  },
  computed: {
    activeFrame() {
      return this.activeFrames[this.currentFloor]
    },
    activeFrameIndex() {
      return this.activeFrames[this.currentFloor] - 1
    },
    mainBgColor() {
        const color = this.apartmentMeta?.BackgroundColor
        if (!color) return this.investmentMeta.mainBgColor
        if (color.startsWith('#')) return color
        if (color.length == 6) return `#${color}`
        return this.investmentMeta.mainBgColor
    },
    roundedZoomValue() {
      if (this.desktop) {
        return this.scaling.defaultZoom
      } else {
        if (this.isLandscapePosition) {
          return Math.round((this.scaling.defaultLandscapeMobileZoom + Number.EPSILON) * 100) / 100
        } else {
          return Math.round((this.scaling.defaultPortraitMobileZoom + Number.EPSILON) * 100) / 100
        }
      }
    },
    loadingProgress() {
      const progressValue = Math.round((parseInt(this.loadingProgressData.loadedImagesCount) / parseInt(this.loadingProgressData.allImagesCount)) * 100)
      return `${isNaN(progressValue) ? 0 : progressValue}%`
    },
    desktop() {
      return window.innerWidth > 900
    },
    firstLoadingStepImagesLoaded() {
      return this.loadingSteps[0].allImagesLoaded
    },
    currentLoadingImagesLoaded() {
      return this.loadingSteps[this.currentLoadingStep].allImagesLoaded
    },
    isLandscapePosition() {
      return this.isLandscape
    },
    isTouchScreen() {
      return window.matchMedia("(pointer: coarse)").matches
    },
    isFullScreenMode() {
      return this.isFullScreen
    },
    zoomDesktop() {
      return this.scaling.defaultZoom
    },
    userIsNotUsingFirefox() {
      return navigator.userAgent.indexOf('Firefox') === -1
    },
  },
  props: {
    investmentMeta: {
      type: Object,
      default: () => {},
    },
    apartmentMeta: {
      type: Object,
      default: () => {},
    },
    apartmentNumber: {
      type: String,
    },
    apartmentMappingName: {
      type: String,
    },
    loading: {
      type: Number
    }
  },
  watch: {
    currentFloor(cur, prev) {
      this.previousFloor = prev
      this.setCompassValue()
    },
    zoomDesktop(cur) {
      if (cur === 1.2) {
        this.centerScrollbars(true)
        this.imageTransformOrigin = "left top"
      } else if (cur > 1.2) {
        this.centerScrollbars()
        this.imageTransformOrigin = "left top"
      } else {
        this.imageTransformOrigin = "center"
        this.$refs.frameWrappers.map((frame) => (frame.style.overflow = `hidden`))
      }
    },
    activeFrame(cur, prev) {
      if (!prev) {
        return
      }
      this.setCompassValue()
    },
    firstLoadingStepImagesLoaded() {
      if (this.firstLoadingStepImagesLoaded) {
        this.changeFloorWithAnimation()

        if (this.loading > 0) this.$emit('update-loading', 0)
      }
    },
    loadingProgressData() {
      this.$emit("loading-progress", this.loadingProgress)
    },
  },
  methods: {
    getCompassValuesInArr() {
      const arrOfFloorsWithCompassValues = []

      for (let i = 0; i < this.apartmentFloorsQuantity; i++) {
        const from = this.apartmentMeta.compassDefaultRotations[i]
        const to = this.apartmentMeta.compassDefaultRotations[i] + 360
        const step = 360 / this.allFramesCount

        arrOfFloorsWithCompassValues.push(
          [...Array(Math.floor((to - from) / step) + 1)].map((_, i) => {
            if (from + i * step >= 360) {
              return from + i * step - 360
            } else {
              return from + i * step
            }
          })
        )
      }

      return arrOfFloorsWithCompassValues
    },
    setCompassValue() {
      this.compassRotate = this.arrOfCompassValues[this.currentFloor][this.activeFrameIndex]
    },
    prev() {
      if (this.rotating) return
      this.rotating = true
      this.toPrev()
    },
    next() {
      if (this.rotating) return
      this.rotating = true
      this.toNext()
    },
    toNext() {
      const rotation = setInterval(() => {
        this.startRotatingLeft()
      }, this.framesIntervalMs)
      this.endRotateByButton(rotation)
    },
    toPrev() {
      const rotation = setInterval(() => {
        this.startRotatingRight()
      }, this.framesIntervalMs)
      this.endRotateByButton(rotation)
    },
    endRotateByButton(rotation) {
      setTimeout(() => {
        this.rotating = false
        clearInterval(rotation)
      }, (this.framesIntervalMs * this.allFramesCount) / this.quantityOfViews)
    },
    move(e) {
      if (this.captureToggle && !this.rotating) {
        const movementX = this.loadedFrames.length === this.investmentMeta.frames.count ?
          getAbsoluteMovementX(e.movementX, e.clientX, this.animation.mouse.lastClientX, this.resetLastClientX) : 0

        this.animation.mouse.lastClientX = e.clientX

        // to left
        if(userIsSwipingLeft(e.movementX)) {
          if(movementX > this.animation.pixelsPerOneFrame) {
            this.swipeManyFrames(movementX, 'NEXT')
          }

          this.startRotatingLeft()
        }

        // To right
        if(userIsSwipingRight(e.movementX)) {
          if(movementX > this.animation.pixelsPerOneFrame) {
            this.swipeManyFrames(movementX, 'PREV')
          }

          this.startRotatingRight()
        }
      }
    },
    captureOn(event) {
      if (event.target.classList.contains("button") || event.target.classList.contains("compass") || event.target.tagName.toLowerCase() === "span") {
        return
      }
      this.captureToggle = true
      this.$refs.apartmentContainer.classList.add("move")
      this.closeRotateTooltip()
    },
    captureOff() {
      this.captureToggle = false
      this.resetLastClientX()
      this.$refs.apartmentContainer.classList.remove("move")
    },
    startRotatingRight() {
      if (this.activeFrame - this.currentLoadingImagesModuloN >= 1) {
        this.$set(this.activeFrames, this.currentFloor, this.activeFrame - this.currentLoadingImagesModuloN)
      } else {
        let lastFrame = this.allFramesCount;
        if (this.currentLoadingImagesModuloN > 1) {
          while (lastFrame % this.currentLoadingImagesModuloN !== 1) {
              lastFrame--;
          }
        }
        this.$set(this.activeFrames, this.currentFloor, lastFrame)
      }
    },
    startRotatingLeft() {
      if (this.activeFrame + this.currentLoadingImagesModuloN <= this.allFramesCount) {
        this.$set(this.activeFrames, this.currentFloor, this.activeFrame + this.currentLoadingImagesModuloN)
      } else {
        this.$set(this.activeFrames, this.currentFloor, 1)
      }
    },
    isOneFingerActive(e) {
      return e.touches.length <= 1
    },
    countFingersDistance(e) {
      return Math.hypot(e.touches[0].pageX - e.touches[1].pageX, e.touches[0].pageY - e.touches[1].pageY)
    },
    startScaleByPinch(e) {
      this.pinchScaling.isActive = true
      this.pinchScaling.startFingersDistance = this.countFingersDistance(e)
    },
    scaleByPinch(e) {
      const newFingersDistance = this.countFingersDistance(e)
      if (newFingersDistance < this.pinchScaling.startFingersDistance) {
        this.zoomOut(this.pinchScaling.zoomGap)
      } else if (newFingersDistance > this.pinchScaling.startFingersDistance) {
        this.pinchScaling.zoomGap
        this.zoomIn(this.pinchScaling.zoomGap)
      }
    },
    endScaleByPinch() {
      this.pinchScaling.isActive = false
      this.pinchScaling.startFingersDistance = 0
    },

    touchStart() {
      this.closeRotateTooltip()
      if (this.isOneFingerActive(event)) {
        this.captureToggle = true
        this.x = event.touches[0].clientX
      } else {
        this.startScaleByPinch(event)
      }
    },
    touchMove(e) {
      if (this.isOneFingerActive(e)) {
        if (this.captureToggle) {
          e.preventDefault()
          const touch = e.touches[0]

          if (this.animation.touch.previousTouch) {
            // calc equivalent of MouseEvent movementX for TouchEvent
            this.animation.touch.movementX = touch.pageX - this.animation.touch.previousTouch.pageX
          }

          this.animation.touch.previousTouch = touch

          if (this.animation.touch.movementX) {
            const movementX = this.loadedFrames.length === this.investmentMeta.frames.count ?
              getAbsoluteMovementX(this.animation.touch.movementX, touch.clientX, this.animation.touch.lastClientX, this.resetLastClientX) : 0

            this.animation.touch.lastClientX = touch.clientX

            //  To left
            if(userIsSwipingLeft(this.animation.touch.movementX)) {
              if(movementX > this.animation.pixelsPerOneFrame) {
                this.swipeManyFrames(movementX, 'NEXT')
              }

              this.startRotatingLeft()
            }

            //  To right
            if(userIsSwipingRight(this.animation.touch.movementX)) {
              if(movementX > this.animation.pixelsPerOneFrame) {
                this.swipeManyFrames(movementX, 'PREV')
              }

              this.startRotatingRight()
            }
          }
        }
      }

      if (this.pinchScaling.isActive) {
        this.scaleByPinch(e)
      }
    },
    swipeManyFrames(movementX = 0, direction = 'NEXT') {
      const framesToSwipe = Math.floor((movementX / this.animation.pixelsPerOneFrame))
      direction = direction.toUpperCase()
      for(let i=0; i < framesToSwipe; i++) {
        if(direction === 'NEXT') {
          this.startRotatingLeft()
          continue
        }
        if(direction === 'PREV') {
          this.startRotatingRight()
        }
      }
    },
    resetLastClientX() {
      this.animation.mouse.lastClientX = null
      this.animation.touch.lastClientX = null
    },
    touchEnd() {
      if (this.isOneFingerActive(event)) {
        this.captureToggle = false
      } else {
        this.endScaleByPinch()
      }

      this.animation.touch.movementX = null
      this.animation.touch.lastClientX = null
      this.animation.touch.previousTouch = null
    },

    zoomIn(gap) {
      if (this.canBeZoomedIn()) {
        if (this.desktop) {
          this.scaling.defaultZoom = this.scaling.defaultZoom + gap
        } else {
          if (this.isLandscapePosition) {
            this.scaling.defaultLandscapeMobileZoom = this.scaling.defaultLandscapeMobileZoom + gap
          } else {
            this.scaling.defaultPortraitMobileZoom = this.scaling.defaultPortraitMobileZoom + gap
          }
        }
      }
    },
    zoomOut(gap) {
      if (this.canBeZoomedOut()) {
        if (this.desktop) {
          this.scaling.defaultZoom = this.scaling.defaultZoom - gap
        } else {
          if (this.isLandscapePosition) {
            this.scaling.defaultLandscapeMobileZoom = this.scaling.defaultLandscapeMobileZoom - gap
          } else {
            this.scaling.defaultPortraitMobileZoom = this.scaling.defaultPortraitMobileZoom - gap
          }
        }
      }
    },
    canBeZoomedIn() {
      if (this.desktop) {
        return this.scaling.defaultZoom < this.scaling.maxZoom
      } else {
        if (this.isLandscapePosition) {
          return this.scaling.defaultLandscapeMobileZoom < this.scaling.maxLandscapeMobileZoom
        } else {
          return this.scaling.defaultPortraitMobileZoom < this.scaling.maxPortraitMobileZoom
        }
      }
    },
    canBeZoomedOut() {
      if (this.desktop) {
        return this.scaling.defaultZoom > this.scaling.minZoom
      } else {
        if (this.isLandscapePosition) {
          return this.scaling.defaultLandscapeMobileZoom > this.scaling.minLandscapeMobileZoom
        } else {
          return this.scaling.defaultPortraitMobileZoom > this.scaling.minPortraitMobileZoom
        }
      }
    },

    centerScrollbars(delay = false) {
      const scrollTop = (window.innerHeight * this.scaling.defaultZoom - window.innerHeight) / 2
      const scrollLeft = (window.innerWidth * this.scaling.defaultZoom - window.innerWidth) / 2

      const apartmentFrame = this.$refs.frameWrappers
      apartmentFrame.map((frame) => (frame.style.overflow = `auto`))

      if (!delay) {
        apartmentFrame.map((frame) => (frame.scrollTop = scrollTop))
        apartmentFrame.map((frame) => (frame.scrollLeft = scrollLeft))
        return
      }

      setTimeout(() => {
        apartmentFrame.map((frame) => (frame.scrollTop = scrollTop))
        apartmentFrame.map((frame) => (frame.scrollLeft = scrollLeft))
      }, 0)
    },

    getFramesResolution() {
      let mainContainer = document.querySelector(`#main`)
      let mainContainerWidth = mainContainer.style.width

      let framesResolutions = this.investmentMeta.frames.breakpoints
      const result = []
      let index = 1
      framesResolutions.forEach((framesResolution) => {
        framesResolution = parseInt(framesResolution)
        mainContainerWidth = parseInt(mainContainerWidth, 10)

        if (window.innerWidth > 900) {
          if (window.innerWidth <= framesResolution) {
            if (!result.length) {
              result.push(framesResolution)
            }
            return
          }
          if (index === framesResolutions.length) {
            if (window.innerWidth >= framesResolution) {
              result.push(framesResolution)
            }
          }

          index++
        } else {
          result.push(1280)
          return
        }
      })

      if (result.length === 0) {
        result.push(framesResolutions[0])
      }
      return result[0]
    },

    loadFrames(newModuloValue, forFirstTime = false) {
      const FRAME_BASE_PATH_IMAGE_NUMBER_REPLACEMENT_STRING = "1.jpg"
      let floor = "/"

      if (this.apartmentFloorsQuantity > 1) {
        floor = `/${this.currentFloor}/`
      }

      if (forFirstTime) {
        this.frames.map((floor, i) => {
          if (i !== this.currentFloor) {
            let frame = new Image()
            frame.src = `${this.getFramesForFloor(i)}?${verQuery}`
            this.frames[i] = [frame]
          }
        })
      }

      const frameResolution = this.getFramesResolution()
      let frameBasePath = `${this.investmentMeta.endpointPath}/apartments/${this.apartmentMappingName}/${this.investmentMeta.frames.basePath}`
      frameBasePath = frameBasePath.replace("FRAMES_RESOLUTION", frameResolution)
      frameBasePath = frameBasePath.replace("/FLOOR/", floor)
      const framesCount = this.investmentMeta.frames.count

      this.allFramesCount = framesCount

      let nextI = 1
      let currentModuloValue = typeof newModuloValue !== "undefined" ? newModuloValue : this.currentLoadingImagesModuloN

      for (let i = 1; i <= framesCount; i++) {
        if (i === nextI) {
          nextI += currentModuloValue
        } else {
          continue
        }

        if (typeof this.frames[this.currentFloor][i - 1] !== "undefined") {
          continue
        }

        let frame = new Image()

        frame.onload = () => {
          this.frameLoaded(i - 1, newModuloValue)
          this.loadedFramesCount = this.loadedFramesCount + 1
        }

        frame.src = `${frameBasePath.replace(FRAME_BASE_PATH_IMAGE_NUMBER_REPLACEMENT_STRING, `${i.toString()}.jpg`)}?${verQuery}`
        this.loadingSteps[this.currentLoadingStep].loadingFramesCount++
        this.frames[this.currentFloor][i - 1] = frame
      }
    },
    frameLoaded(index, newModuloValue) {
      this.loadedFrames[index] = true
      this.checkIfAllCurrentLoadingImagesAreLoaded(newModuloValue)
    },
    checkIfAllCurrentLoadingImagesAreLoaded(newModulo) {
      if (this.loadingSteps[this.currentLoadingStep].allImagesLoaded) {
        return
      }

      let loadedFrames = this.loadedFrames.filter((loadedFrame) => loadedFrame === true)
      let loadedFramesCount = 0
      if (loadedFrames && loadedFrames.length) {
        loadedFramesCount = loadedFrames.length
      }

      if (this.currentLoadingStep > 0) {
        let currentLoadingStep = this.currentLoadingStep
        while (currentLoadingStep > 0) {
          currentLoadingStep--
          loadedFramesCount -= this.loadingSteps[currentLoadingStep].loadingFramesCount
        }
      }

      const loadingFramesCount = this.loadingSteps[this.currentLoadingStep].loadingFramesCount

      this.loadingProgressData = {
        loadedImagesCount: loadedFramesCount,
        allImagesCount: loadingFramesCount,
      }

      if (loadingFramesCount === loadedFramesCount) {
        this.loadingSteps[this.currentLoadingStep].allImagesLoaded = true

        if (typeof newModulo !== "undefined") {
          this.currentLoadingImagesModuloN = newModulo
        }

        if (this.currentLoadingStep < this.loadingSteps.length - 1) {
          let newModuloValue = this.currentLoadingImagesModuloN === 4 ? 2 : 1

          this.currentLoadingStep++

          this.loadFrames(newModuloValue)
        }
      }
    },
    toggleOrientationMode() {
      let orientation
      try {
        orientation = window.screen.orientation.type
      } catch (e) {
        orientation = window.orientation
      }
      if (orientation === "portrait-primary" || orientation === 0) {
        this.isLandscape = false
      } else if (orientation === "landscape-primary" || orientation === 90) {
        this.pinchScaling.zoomGap = 0.05
        this.isLandscape = true
      }
    },
    closeRotateTooltip() {
      this.isTooltipRotateVisible = false
    },
    toggleFullScreenMode() {
      this.isFullScreen = !this.isFullScreen
      if (this.isFullScreen) {
        const elem = document.querySelector("#apartment")
        if (elem.requestFullscreen) {
          elem.requestFullscreen()
        } else if (elem.mozRequestFullScreen) {
          elem.mozRequestFullScreen()
        } else if (elem.webkitRequestFullscreen) {
          elem.webkitRequestFullscreen()
        } else if (elem.msRequestFullscreen) {
          elem.msRequestFullscreen()
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen()
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
        }
      }
    },
    exitFullScreenByEsc() {
      if (!document.fullscreenElement && !document.webkitIsFullScreen && !document.mozFullScreen && !document.msFullscreenElement) {
        this.isFullScreen = false
      }
    },
    changeFloor(floor) {
      let wasFloorLoaded = this.floorsWereLoaded.includes(floor)

      if (!wasFloorLoaded) {
        this.$emit('update-loading', 1)
        this.currentLoadingImagesModuloN = 4

        this.currentFloor = floor
        this.loadedFrames = []
        this.loadedFramesCount = 0
        this.allFramesCount = 0
        this.loadingSteps = [
          {
            allImagesLoaded: false,
            loadingFramesCount: 0,
          },
          {
            allImagesLoaded: false,
            loadingFramesCount: 0,
          },
          {
            allImagesLoaded: false,
            loadingFramesCount: 0,
          },
        ]
        this.currentLoadingStep = 0
        this.loadingProgressData = {
          loadedImagesCount: 0,
          allImagesCount: 0,
        }
        this.loadFrames()

        this.floorsWereLoaded.push(floor)
      } else {
        this.currentFloor = floor
        setTimeout(() => {
          this.changeFloorWithAnimation()
          this.centerFramesAfterFloorsToggling()
        }, 0)
      }
    },
    centerFramesAfterFloorsToggling() {
      if (this.zoomDesktop > 1) {
        this.centerScrollbars(true)
      }
    },
    getFramesForFloor(floor = false) {
      let frameBasePath = `${this.investmentMeta.endpointPath}/apartments/${this.apartmentMappingName}/${this.investmentMeta.frames.basePath}`
      const frameResolution = this.getFramesResolution()
      frameBasePath = frameBasePath.replace("FRAMES_RESOLUTION", frameResolution)
      let floorString = "/"
      if (floor !== false) {
        floorString = `/${floor}/`
      }
      frameBasePath = frameBasePath.replace("/FLOOR/", floorString)
      const FRAME_BASE_PATH_IMAGE_NUMBER_REPLACEMENT_STRING = "1.jpg"

      const frameSrc = frameBasePath.replace(FRAME_BASE_PATH_IMAGE_NUMBER_REPLACEMENT_STRING, `${this.activeFrames[floor ? floor : 0].toString()}.jpg`)

      return frameSrc
    },
    changeFloorWithAnimation() {
      if (this.apartmentFloorsQuantity > 1) {
        if (this.previousFloor < this.currentFloor) {
          const dif = this.currentFloor - this.previousFloor
          const newTranslateYValue = this.currentTranslateYFrames + (100 / this.apartmentFloorsQuantity) * dif
          this.currentTranslateYFrames = Math.round((newTranslateYValue + Number.EPSILON) * 100) / 100
        } else if (this.currentFloor < this.previousFloor) {
          const dif = this.previousFloor - this.currentFloor
          const newTranslateYValue = this.currentTranslateYFrames - (100 / this.apartmentFloorsQuantity) * dif
          this.currentTranslateYFrames = Math.round((newTranslateYValue + Number.EPSILON) * 100) / 100
        }
      }
    },
    getFloorStartFromUrl() {
      const key = "floor-start"

      const urlParams = new URLSearchParams(window.location.search)

      if (urlParams.has(key)) {
        const floor = +urlParams.get(key)
        if (floor < this.apartmentFloorsQuantity && floor >= 0) {
          return floor
        } else if (floor >= this.apartmentFloorsQuantity) {
          return this.apartmentFloorsQuantity - 1
        } else {
          return 0
        }
      } else {
        return 0
      }
    },
    handleResize() {
      clearTimeout(this.resizeDebounceTimeout)
      this.resizeDebounceTimeout = setTimeout(() => {
        const newRes = this.getFramesResolution()
        if (this.currentFrameResolution >= newRes) {
          return
        }
        this.currentFrameResolution = newRes
        this.$emit('update-loading', 1)
        this.frames[this.currentFloor] = []
        this.loadedFrames = []
        this.loadedFramesCount = 0
        this.allFramesCount = 0
        this.loadingSteps = [
          {
            allImagesLoaded: false,
            loadingFramesCount: 0,
          },
          {
            allImagesLoaded: false,
            loadingFramesCount: 0,
          },
          {
            allImagesLoaded: false,
            loadingFramesCount: 0,
          },
        ]
        this.currentLoadingStep = 0
        this.loadingProgressData = {
          loadedImagesCount: 0,
          allImagesCount: 0,
        }
        this.currentLoadingImagesModuloN = 4
        this.loadFrames()
      }, 200)
    }
  },
  created() {
    const currentFloor = this.getFloorStartFromUrl()
    this.currentFloor = currentFloor

    const activeFrames = []
    for (let i = 0; i < this.apartmentFloorsQuantity; i++) {
      activeFrames.push(this.startFromFrame)
      this.frames.push([])
      this.floorsArr.push(i)
    }

    this.floorsWereLoaded.push(this.currentFloor)
    this.activeFrames = activeFrames

    this.loadFrames(this.currentLoadingImagesModuloN, true)
    this.toggleOrientationMode()

    this.arrOfCompassValues = this.getCompassValuesInArr()
    this.setCompassValue()

    this.currentFrameResolution = this.getFramesResolution()
  },
  mounted() {
    document.addEventListener("fullscreenchange", this.exitFullScreenByEsc)
    document.addEventListener("webkitfullscreenchange", this.exitFullScreenByEsc)
    document.addEventListener("mozfullscreenchange", this.exitFullScreenByEsc)
    document.addEventListener("MSFullscreenChange", this.exitFullScreenByEsc)

    window.addEventListener("orientationchange", this.toggleOrientationMode)
    window.addEventListener("resize", this.handleResize)
  },
  beforeDestroy() {
    document.removeEventListener("fullscreenchange", this.exitFullScreenByEsc)
    document.removeEventListener("webkitfullscreenchange", this.exitFullScreenByEsc)
    document.removeEventListener("mozfullscreenchange", this.exitFullScreenByEsc)
    document.removeEventListener("MSFullscreenChange", this.exitFullScreenByEsc)

    window.removeEventListener("orientationchange", this.toggleOrientationMode)
    window.removeEventListener("resize", this.handleResize)
  },
  components: {
    Compass,
    Button,
    Buttons,
    ButtonGroup,
    LoadingFrames,
    RotateTooltip,
    YslabLogo
  },
}
</script>

<style lang="scss" scoped>
@import "~@/styles/Apartment.scss";
</style>
